/**
 * Divisions Store (Zustand)
 *
 * Manages division definitions with Firestore as source of truth.
 * Divisions are a grouping layer above locations — each location
 * belongs to one division, and users are assigned to divisions.
 *
 * The selectedDivisionId is persisted to localStorage for session continuity.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { DivisionDefinition } from '../types/divisions.types';

const STORAGE_KEY = 'selectedDivision';

interface DivisionsState {
  // Data
  divisions: DivisionDefinition[];
  selectedDivisionId: string | null;

  // Loading state
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Actions
  setDivisions: (divisions: DivisionDefinition[]) => void;
  setSelectedDivision: (divisionId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;

  // Helpers
  getActiveDivision: () => DivisionDefinition | undefined;
  getDivisionLocationIds: (divisionId: string) => string[];
  getUserDivisions: (userDivisionIds: string[]) => DivisionDefinition[];
}

/**
 * Read initial selectedDivisionId from localStorage
 */
function getPersistedDivisionId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export const useDivisionsStore = create<DivisionsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      divisions: [],
      selectedDivisionId: getPersistedDivisionId(),
      isLoading: true,
      error: null,
      isInitialized: false,

      // Actions
      setDivisions: (divisions) => set({ divisions, error: null }),

      setSelectedDivision: (divisionId) => {
        set({ selectedDivisionId: divisionId });
        // Persist to localStorage
        try {
          if (divisionId) {
            localStorage.setItem(STORAGE_KEY, divisionId);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch {
          // Ignore storage errors
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setInitialized: (initialized) => set({ isInitialized: initialized }),

      // Helpers
      getActiveDivision: () => {
        const { divisions, selectedDivisionId } = get();
        return divisions.find(
          (d) => d.id === selectedDivisionId && d.isActive
        );
      },

      getDivisionLocationIds: (divisionId) => {
        const division = get().divisions.find((d) => d.id === divisionId);
        return division?.locationIds || [];
      },

      getUserDivisions: (userDivisionIds) => {
        const { divisions } = get();
        return divisions
          .filter((d) => d.isActive && userDivisionIds.includes(d.id))
          .sort((a, b) => a.sortOrder - b.sortOrder);
      },
    }),
    { name: 'DivisionsStore' }
  )
);

/**
 * Selector hooks for common use cases
 */
export const useDivisions = () =>
  useDivisionsStore((state) => state.divisions);

export const useSelectedDivisionId = () =>
  useDivisionsStore((state) => state.selectedDivisionId);

export const useSelectedDivision = () =>
  useDivisionsStore((state) => {
    const { divisions, selectedDivisionId } = state;
    return divisions.find(
      (d) => d.id === selectedDivisionId && d.isActive
    ) || null;
  });

export const useDivisionsInitialized = () =>
  useDivisionsStore((state) => state.isInitialized);



export const getActiveDivisions = () =>
  useDivisionsStore.getState().divisions.filter((d) => d.isActive);


